<template>
    <card v-if="arEnabled" cardBodyClasses="p-0">
        <b-row>
            <b-col cols="12">
                <h4 class="card-subtitle" style="padding-top: 15px; padding-left: 15px">
                    Accounts Receivable
                </h4>
            </b-col>
        </b-row>

        <template v-if="hasArViewPermission">
            <div v-if="loading" class="text-center p-3 d-flex align-items-center justify-content-center"
                style="gap: 6px;">
                <b-spinner small />
                Loading account...
            </div>

            <div v-else-if="!accounts.length" class="text-center p-2">
                <div>No AR Account On File</div>
                <b-button v-if="canCreate" size="sm" variant="outline-primary" class="mt-2" @click="openCreate">
                    Create AR Account
                </b-button>
            </div>

            <div class="p-2 pb-0" v-else>
                <div v-for="acct in accounts" :key="acct.id" class="mb-3">
                    <b-row class="mb-1">
                        <b-col cols="7">
                            <strong>{{ acct.accountNumber }}</strong>
                            <b-badge :variant="statusVariant(acct.status)" class="ml-1">
                                {{ statusLabel(acct.status) }}
                            </b-badge>
                            <b-badge v-if="acct.autoPayEnabled" variant="info" class="ml-1">
                                Auto-Pay
                            </b-badge>
                        </b-col>
                        <b-col cols="5" class="text-right">
                            <strong>Balance:</strong> {{ currency(acct.currentBalance) }}
                        </b-col>
                    </b-row>

                    <b-table-simple small bordered class="mb-1">
                        <thead>
                            <tr>
                                <th>Current</th>
                                <th>31-60</th>
                                <th>61-90</th>
                                <th>91-120</th>
                                <th>120+</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ currency(agingFor(acct.id).agingCurrent) }}</td>
                                <td>{{ currency(agingFor(acct.id).agingBucket1) }}</td>
                                <td>{{ currency(agingFor(acct.id).agingBucket2) }}</td>
                                <td>{{ currency(agingFor(acct.id).agingBucket3) }}</td>
                                <td>{{ currency(agingFor(acct.id).agingBucket4Plus) }}</td>
                            </tr>
                        </tbody>
                    </b-table-simple>

                    <div class="d-flex justify-content-between align-items-center">
                        <span style="cursor: pointer" @click="toggleLedger(acct.id)">
                            <i class="fas fa-list-ul"></i> Ledger
                        </span>
                        <div>
                            <b-button v-if="canTakePayments" size="sm" variant="outline-primary" class="mr-1"
                                @click="openPayments(acct.id)">
                                Payments
                            </b-button>
                            <b-button v-if="showAutoPay" size="sm"
                                :variant="acct.autoPayEnabled ? 'success' : 'outline-primary'" class="mr-1"
                                @click="openAutoPay(acct.id)">
                                Auto-Pay
                            </b-button>
                            <b-button v-if="canManage" size="sm" variant="outline-secondary" @click="openManage(acct.id)">
                                Manage
                            </b-button>
                        </div>
                    </div>
                    <b-collapse :id="`ar-ledger-${acct.id}`" accordion="ar-ledger-accordion" role="tabpanel">
                        <b-table-simple small class="mb-0 mt-1">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Reason</th>
                                    <th class="text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="!ledgerFor(acct.id).length">
                                    <td colspan="4" class="text-center">No transactions</td>
                                </tr>
                                <tr v-for="t in ledgerFor(acct.id)" :key="t.id">
                                    <td>{{ formatDate(t.created) }}</td>
                                    <td>{{ transactionTypeLabel(t.transactionType) }}</td>
                                    <td>{{ ledgerReason(t) }}</td>
                                    <td class="text-right">{{ currency(t.amount) }}</td>
                                </tr>
                            </tbody>
                        </b-table-simple>
                    </b-collapse>
                </div>
            </div>
        </template>

        <b-alert :show="!hasArViewPermission" variant="warning" class="my-2" style="margin: 15px;">
            <strong>
                You do not have permissions to view Accounts Receivable.
            </strong>
        </b-alert>

        <ARAccountManageModal v-model="showManageModal" :patient="patient" :account-id="manageAccountId"
            @saved="onSaved" />
        <ARPaymentsModal v-model="showPaymentsModal" :account-id="paymentsAccountId" @saved="onSaved" />
        <ARAutoPayModal v-model="showAutoPayModal" :account-id="autoPayAccountId"
            :auto-pay-available="arAutoPayEnabled && arPortalPaymentCC" @saved="onSaved" />
    </card>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import {
    ARAccountSummary,
    ARAging,
    ARLedgerEntry,
    ARPaymentView,
    arAccountStatusLabel,
    arTransactionTypeLabel,
} from "@/models/AR";
import axios from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import ARAccountManageModal from "@/components/Patient/ARAccountManageModal.vue";
import ARPaymentsModal from "@/components/Patient/ARPaymentsModal.vue";
import ARAutoPayModal from "@/components/Patient/ARAutoPayModal.vue";

@Component({
    name: "AccountsReceivableCard",
    components: { ARAccountManageModal, ARPaymentsModal, ARAutoPayModal },
})
export default class AccountsReceivableCard extends Vue {
    @Prop() private patient!: Patient;

    private arEnabled = false;
    private arAutoPayEnabled = false;
    private arPortalPaymentCC = false;
    private loading = false;
    private accounts: ARAccountSummary[] = [];
    private agingByAccount: Record<number, ARAging> = {};
    private ledgerByAccount: Record<number, ARLedgerEntry[]> = {};
    private paymentByTransaction: Record<number, ARPaymentView> = {};
    private showManageModal = false;
    private manageAccountId: number | null = null;
    private showPaymentsModal = false;
    private paymentsAccountId: number | null = null;
    private showAutoPayModal = false;
    private autoPayAccountId: number | null = null;

    get hasArViewPermission(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Account/View");
    }

    get canCreate(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Account/Create");
    }

    get canManage(): boolean {
        const managePermissions = [
            "Accounts Receivable/Account/Edit",
            "Accounts Receivable/Account/Close",
            "Accounts Receivable/Credit Limit/Override Account Limit",
        ];
        return this.$permissions.some(p => managePermissions.includes(p.permissionCodeName));
    }

    openCreate(): void {
        this.manageAccountId = null;
        this.showManageModal = true;
    }

    openManage(accountId: number): void {
        this.manageAccountId = accountId;
        this.showManageModal = true;
    }

    // Auto-pay enrollment is an account edit, and only surfaces when the pharmacy has the
    // auto-pay feature enabled. The endpoint enforces the same rules server-side.
    get showAutoPay(): boolean {
        return this.arAutoPayEnabled
            && this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Account/Edit");
    }

    openAutoPay(accountId: number): void {
        this.autoPayAccountId = accountId;
        this.showAutoPayModal = true;
    }

    get canTakePayments(): boolean {
        const paymentPermissions = [
            "Accounts Receivable/Payment/Post",
            "Accounts Receivable/Payment/Refund",
            "Accounts Receivable/Transaction/Post Adjustment",
            "Accounts Receivable/Transaction/Write Off",
        ];
        return this.$permissions.some(p => paymentPermissions.includes(p.permissionCodeName));
    }

    openPayments(accountId: number): void {
        this.paymentsAccountId = accountId;
        this.showPaymentsModal = true;
    }

    async onSaved(): Promise<void> {
        this.ledgerByAccount = {};
        this.agingByAccount = {};
        this.paymentByTransaction = {};
        await this.loadAccounts();
    }

    async mounted(): Promise<void> {
        await this.loadConfiguration();
        if (this.arEnabled && this.hasArViewPermission && this.patient?.id) {
            await this.loadAccounts();
        }
    }

    @Watch("patient")
    async onPatientChanged(newPatient: Patient, oldPatient: Patient): Promise<void> {
        if (newPatient?.id === oldPatient?.id) return;
        this.accounts = [];
        this.agingByAccount = {};
        this.ledgerByAccount = {};
        if (this.arEnabled && this.hasArViewPermission && newPatient?.id) {
            await this.loadAccounts();
        }
    }

    private async loadConfiguration(): Promise<void> {
        // These flags are independent, so fetch them together rather than serially.
        const [arEnabled, arAutoPayEnabled, arPortalPaymentCC] = await Promise.all([
            axios.get<boolean>("/configuration/enableAR").then(r => r.data === true).catch(() => false),
            axios.get<boolean>("/configuration/enableArAutoPay").then(r => r.data === true).catch(() => false),
            axios.get<boolean>("/configuration/arPortalPaymentCC").then(r => r.data === true).catch(() => false),
        ]);
        this.arEnabled = arEnabled;
        this.arAutoPayEnabled = arAutoPayEnabled;
        this.arPortalPaymentCC = arPortalPaymentCC;
    }

    private async loadAccounts(): Promise<void> {
        if (!this.patient?.id) return;
        try {
            this.loading = true;
            const response = await axios.get<ARAccountSummary[]>(`/AR/patients/${this.patient.id}/accounts`);
            this.accounts = response.data ?? [];
            await Promise.all(this.accounts.map(a => this.loadAging(a.id)));
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    private async loadAging(accountId: number): Promise<void> {
        try {
            const response = await axios.get<ARAging>(`/AR/accounts/${accountId}/aging`);
            this.$set(this.agingByAccount, accountId, response.data ?? {});
        } catch {
            // Aging is non-critical for the card; ignore failures.
        }
    }

    async toggleLedger(accountId: number): Promise<void> {
        this.$root.$emit("bv::toggle::collapse", `ar-ledger-${accountId}`);
        if (this.ledgerByAccount[accountId]) return;
        try {
            // Payments carry the method/check#/reference used to label payment rows in the ledger.
            const [ledgerResponse, paymentsResponse] = await Promise.all([
                axios.get<ARLedgerEntry[]>(`/AR/accounts/${accountId}/ledger`),
                axios.get<ARPaymentView[]>(`/AR/accounts/${accountId}/payments`),
            ]);
            this.$set(this.ledgerByAccount, accountId, ledgerResponse.data ?? []);
            for (const payment of paymentsResponse.data ?? []) {
                if (payment.arTransactionId != null) {
                    this.$set(this.paymentByTransaction, payment.arTransactionId, payment);
                }
            }
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    // The reason column shows the transaction's reason code, falling back to the payment method
    // (Cash, Check <#>, CC <ref>) for payment rows that have no reason of their own.
    ledgerReason(entry: ARLedgerEntry): string {
        if (entry.reasonCode) return entry.reasonCode;
        if (entry.transactionType === 9 && entry.id != null) {
            const payment = this.paymentByTransaction[entry.id];
            if (payment) {
                switch (payment.paymentMethod) {
                    case 1: return "Cash";
                    case 2: return `Check ${payment.checkNumber ?? ""}`.trim();
                    case 3: return `CC ${payment.referenceId ?? ""}`.trim();
                }
            }
        }
        return "";
    }

    agingFor(accountId: number): ARAging {
        return this.agingByAccount[accountId] ?? {};
    }

    ledgerFor(accountId: number): ARLedgerEntry[] {
        return this.ledgerByAccount[accountId] ?? [];
    }

    statusLabel(status?: number): string {
        return arAccountStatusLabel(status);
    }

    transactionTypeLabel(type?: number): string {
        return arTransactionTypeLabel(type);
    }

    statusVariant(status?: number): string {
        switch (status) {
            case 1: return "success";   // Active
            case 2: return "warning";   // On Hold
            case 3: return "secondary"; // Closed
            case 4: return "danger";    // Written Off
            default: return "light";
        }
    }

    currency(value?: number | null): string {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
    }

    formatDate(date?: string): string {
        if (!date) return "";
        return moment.utc(date).local().format("L");
    }
}
</script>

<style scoped></style>
